// src/InventoryManager.js
import React, { useEffect, useMemo, useState } from "react";
import { api, setStoreIdHeader, setUserHeader } from "./api";

/**
 * ALIGNED TO YOUR CURRENT server.php
 *
 * Endpoints:
 *  GET    /server.php/inventory
 *  POST   /server.php/inventory
 *  PUT    /server.php/inventory/{id}
 *  DELETE /server.php/inventory/{id}
 *  POST   /server.php/replenish   body: { id, quantity, note }
 *
 * Server returns rows with keys:
 *  id, store_id, sku, itemname, category, unit,
 *  buyingprice, sellingprice, quantity, reorder_level, is_active
 */

const emptyForm = {
  id: null,
  sku: "",
  itemname: "",
  category: "",
  unit: "",
  buyingprice: "",
  sellingprice: "",
  quantity: "",
  reorder_level: "",
  is_active: 1,
};

export default function InventoryManager({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // filters
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);

  // modal form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  // replenish modal
  const [showRepl, setShowRepl] = useState(false);
  const [replItem, setReplItem] = useState(null);
  const [replQty, setReplQty] = useState("");
  const [replNote, setReplNote] = useState("Replenish");

  const storeId = Number(localStorage.getItem("store_id") || 0);
  const username =
    (localStorage.getItem("username") || "cashier").trim() || "cashier";

  const flash = (m) => {
    setMsg(m);
    clearTimeout(flash._t);
    flash._t = setTimeout(() => setMsg(""), 3500);
  };

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };

  const fmtMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v ?? "");
    return n.toFixed(2);
  };

  const categories = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const c = String(r.category || "").trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return rows.filter((r) => {
      if (onlyActive && Number(r.is_active) !== 1) return false;

      if (cat.trim()) {
        if (String(r.category || "").toLowerCase() !== cat.trim().toLowerCase())
          return false;
      }

      if (onlyLow) {
        const qty = Number(r.quantity || 0);
        const rol = Number(r.reorder_level || 0);
        if (!(qty <= rol)) return false;
      }

      if (!needle) return true;

      const hay = [
        r.id,
        r.sku,
        r.itemname,
        r.category,
        r.unit,
        r.buyingprice,
        r.sellingprice,
        r.quantity,
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(needle);
    });
  }, [rows, q, cat, onlyLow, onlyActive]);

  async function load() {
    if (storeId <= 0) {
      flash("❌ Missing store_id. Login again.");
      return;
    }

    setLoading(true);
    setStoreIdHeader(storeId);
    setUserHeader(username);

    try {
      const res = await api.get("/server.php/inventory");
      const data = res?.data;

      if (!data?.success) {
        flash(`❌ ${data?.message || "Failed to load inventory"}`);
        setRows([]);
      } else {
        const list = Array.isArray(data.rows) ? data.rows : [];
        setRows(list);
      }
    } catch (e) {
      console.error(e);
      flash("❌ Server error while loading inventory");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setForm({ ...emptyForm, is_active: 1 });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setForm({
      id: r.id,
      sku: r.sku ?? "",
      itemname: r.itemname ?? "",
      category: r.category ?? "",
      unit: r.unit ?? "",
      buyingprice: String(r.buyingprice ?? ""),
      sellingprice: String(r.sellingprice ?? ""),
      quantity: String(r.quantity ?? ""),
      reorder_level: String(r.reorder_level ?? ""),
      is_active: Number(r.is_active ?? 1),
    });
    setShowForm(true);
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validateForm = () => {
    if (!String(form.itemname).trim()) return "Item name is required";
    if (toNum(form.buyingprice) !== toNum(form.buyingprice))
      return "Buying price must be a number";
    if (toNum(form.sellingprice) !== toNum(form.sellingprice))
      return "Selling price must be a number";
    if (toNum(form.quantity) !== toNum(form.quantity))
      return "Quantity must be a number";
    if (toNum(form.reorder_level) !== toNum(form.reorder_level))
      return "Reorder level must be a number";
    return "";
  };

  async function saveItem(e) {
    e?.preventDefault?.();
    const err = validateForm();
    if (err) {
      flash(`❌ ${err}`);
      return;
    }

    setLoading(true);
    setStoreIdHeader(storeId);
    setUserHeader(username);

    const payload = {
      sku: String(form.sku || "").trim(),
      itemname: String(form.itemname || "").trim(),
      category: String(form.category || "").trim(),
      unit: String(form.unit || "").trim(),
      buyingprice: Number(form.buyingprice),
      sellingprice: Number(form.sellingprice),
      quantity: Number(form.quantity),
      reorder_level: Number(form.reorder_level),
      is_active: Number(form.is_active) === 1 ? 1 : 0,
    };

    try {
      let res;
      if (form.id) {
        res = await api.put(`/server.php/inventory/${form.id}`, payload);
      } else {
        res = await api.post(`/server.php/inventory`, payload);
      }

      const data = res?.data;
      if (!data?.success) {
        flash(`❌ ${data?.message || "Save failed"}`);
      } else {
        flash("✅ Saved");
        setShowForm(false);
        await load();
      }
    } catch (e) {
      console.error(e);
      flash("❌ Server error while saving");
    } finally {
      setLoading(false);
    }
  }

  async function deleteItem(id) {
    if (!window.confirm("Delete this item?")) return;

    setLoading(true);
    setStoreIdHeader(storeId);
    setUserHeader(username);

    try {
      const res = await api.delete(`/server.php/inventory/${id}`);
      const data = res?.data;
      if (!data?.success) flash(`❌ ${data?.message || "Delete failed"}`);
      else {
        flash("✅ Deleted");
        await load();
      }
    } catch (e) {
      console.error(e);
      flash("❌ Server error while deleting");
    } finally {
      setLoading(false);
    }
  }

  const openReplenish = (r) => {
    setReplItem(r);
    setReplQty("");
    setReplNote("Replenish");
    setShowRepl(true);
  };

  async function doReplenish(e) {
    e?.preventDefault?.();
    const qtyAdd = Number(replQty);

    if (!Number.isFinite(qtyAdd) || qtyAdd <= 0) {
      flash("❌ Enter a valid replenish quantity > 0");
      return;
    }

    setLoading(true);
    setStoreIdHeader(storeId);
    setUserHeader(username);

    try {
      const res = await api.post(`/server.php/replenish`, {
        id: Number(replItem?.id || 0),
        quantity: qtyAdd,
        note: String(replNote || "Replenish").trim(),
      });

      const data = res?.data;
      if (!data?.success) flash(`❌ ${data?.message || "Replenish failed"}`);
      else {
        flash("✅ Replenished (saved for reports)");
        setShowRepl(false);
        await load();
      }
    } catch (e) {
      console.error(e);
      flash("❌ Server error while replenishing");
    } finally {
      setLoading(false);
    }
  }

  return (
    // ✅ FULL WIDTH container
    <div
      style={{
        padding: "24px 40px",
        width: "100%",
        maxWidth: "100%",
        margin: 0,
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {onBack && (
          <button onClick={onBack} style={btn()}>
            ← Back
          </button>
        )}
        <h2 style={{ margin: 0, fontSize: 28 }}>Inventory Manager</h2>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
          <button onClick={load} disabled={loading} style={btn("ghost")}>
            Refresh
          </button>
          <button onClick={openCreate} style={btn("primary")}>
            + Add Item
          </button>
        </div>
      </div>

      {msg && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 12,
            background: "#f3f4f6",
            fontSize: 14,
          }}
        >
          {msg}
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          marginTop: 14,
          display: "grid",
          gridTemplateColumns: "1.6fr 0.8fr 0.7fr 0.7fr",
          gap: 12,
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search SKU / item name / category..."
          style={inp()}
        />

        <select value={cat} onChange={(e) => setCat(e.target.value)} style={inp()}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <label style={chk()}>
          <input
            type="checkbox"
            checked={onlyLow}
            onChange={(e) => setOnlyLow(e.target.checked)}
          />
          <span>Low stock</span>
        </label>

        <label style={chk()}>
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
          />
          <span>Active only</span>
        </label>
      </div>

      {/* Table */}
      <div
        style={{
          marginTop: 16,
          width: "100%",
          overflowX: "auto",
          border: "1px solid #e5e7eb",
          borderRadius: 14,
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f9fafb" }}>
              {[
                "ID",
                "SKU",
                "Item Name",
                "Category",
                "Unit",
                "Buying",
                "Selling",
                "Qty",
                "Reorder",
                "Active",
                "Actions",
              ].map((h) => (
                <th key={h} style={th()}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={11} style={{ padding: 16 }}>
                  Loading...
                </td>
              </tr>
            )}

            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: 16 }}>
                  No items found.
                </td>
              </tr>
            )}

            {!loading &&
              filtered.map((r) => {
                const qty = Number(r.quantity || 0);
                const rol = Number(r.reorder_level || 0);
                const low = qty <= rol;

                return (
                  <tr
                    key={r.id}
                    style={{
                      borderTop: "1px solid #eee",
                      background: low ? "#fff7ed" : "white",
                    }}
                  >
                    <td style={td()}>{r.id}</td>
                    <td style={td()}>{r.sku || "—"}</td>
                    <td style={td()}>
                      <div style={{ fontWeight: 700 }}>{r.itemname}</div>
                    </td>
                    <td style={td()}>{r.category || "—"}</td>
                    <td style={td()}>{r.unit || "—"}</td>
                    <td style={td()}>{fmtMoney(r.buyingprice)}</td>
                    <td style={td()}>{fmtMoney(r.sellingprice)}</td>
                    <td style={td()}>{qty}</td>
                    <td style={td()}>{rol}</td>
                    <td style={td()}>{Number(r.is_active) === 1 ? "Yes" : "No"}</td>

                    {/* ✅ Actions: ONE LINE + BIGGER buttons */}
                    <td style={td()}>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          flexWrap: "nowrap",
                          alignItems: "center",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <button onClick={() => openEdit(r)} style={btn("ghost")}>
                          Edit
                        </button>
                        <button onClick={() => openReplenish(r)} style={btn("primary")}>
                          Replenish
                        </button>
                        <button onClick={() => deleteItem(r.id)} style={btn("danger")}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={modalWrap()}>
          <div style={modal()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h3 style={{ margin: 0 }}>{form.id ? "Edit Item" : "Add Item"}</h3>
              <div style={{ marginLeft: "auto" }}>
                <button onClick={() => setShowForm(false)} style={btn("ghost")}>
                  ✕
                </button>
              </div>
            </div>

            <form onSubmit={saveItem} style={{ marginTop: 12 }}>
              <div style={grid2()}>
                <Field label="SKU">
                  <input value={form.sku} onChange={(e) => setField("sku", e.target.value)} style={inp()} />
                </Field>

                <Field label="Item Name (itemname)">
                  <input
                    value={form.itemname}
                    onChange={(e) => setField("itemname", e.target.value)}
                    style={inp()}
                  />
                </Field>

                <Field label="Category">
                  <input
                    value={form.category}
                    onChange={(e) => setField("category", e.target.value)}
                    style={inp()}
                  />
                </Field>

                <Field label="Unit">
                  <input value={form.unit} onChange={(e) => setField("unit", e.target.value)} style={inp()} />
                </Field>

                <Field label="Buying Price (buyingprice)">
                  <input
                    value={form.buyingprice}
                    onChange={(e) => setField("buyingprice", e.target.value)}
                    style={inp()}
                  />
                </Field>

                <Field label="Selling Price (sellingprice)">
                  <input
                    value={form.sellingprice}
                    onChange={(e) => setField("sellingprice", e.target.value)}
                    style={inp()}
                  />
                </Field>

                <Field label="Quantity (quantity)">
                  <input
                    value={form.quantity}
                    onChange={(e) => setField("quantity", e.target.value)}
                    style={inp()}
                  />
                </Field>

                <Field label="Reorder Level">
                  <input
                    value={form.reorder_level}
                    onChange={(e) => setField("reorder_level", e.target.value)}
                    style={inp()}
                  />
                </Field>

                <label style={{ ...chk(), gridColumn: "1 / span 2" }}>
                  <input
                    type="checkbox"
                    checked={Number(form.is_active) === 1}
                    onChange={(e) => setField("is_active", e.target.checked ? 1 : 0)}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button type="submit" disabled={loading} style={btn("primary")}>
                  {loading ? "Saving..." : "Save"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} style={btn()}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Replenish Modal */}
      {showRepl && (
        <div style={modalWrap()}>
          <div style={modal()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h3 style={{ margin: 0 }}>Replenish Stock</h3>
              <div style={{ marginLeft: "auto" }}>
                <button onClick={() => setShowRepl(false)} style={btn("ghost")}>
                  ✕
                </button>
              </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 13, opacity: 0.85 }}>
              <div>
                <b>Item:</b> {replItem?.itemname} (ID: {replItem?.id})
              </div>
              <div>
                <b>Current Qty:</b> {Number(replItem?.quantity || 0)}
              </div>
            </div>

            <form onSubmit={doReplenish} style={{ marginTop: 12 }}>
              <div style={grid2()}>
                <Field label="Add Quantity">
                  <input value={replQty} onChange={(e) => setReplQty(e.target.value)} style={inp()} />
                </Field>

                <Field label="Note (optional)">
                  <input value={replNote} onChange={(e) => setReplNote(e.target.value)} style={inp()} />
                </Field>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button type="submit" disabled={loading} style={btn("primary")}>
                  {loading ? "Saving..." : "Replenish"}
                </button>
                <button type="button" onClick={() => setShowRepl(false)} style={btn()}>
                  Cancel
                </button>
              </div>
            </form>

            <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
              Calls <code>POST /replenish</code> with <code>{"{ id, quantity, note }"}</code>.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- tiny UI helpers -------------------- */
function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 12, opacity: 0.8 }}>{label}</div>
      {children}
    </div>
  );
}

function btn(variant) {
  const base = {
    border: "1px solid #d1d5db",
    padding: "12px 18px",
    borderRadius: 12,
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: 600,
    background: "white",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    minHeight: "44px",
  };

  if (variant === "primary")
    return { ...base, background: "#111827", color: "white", borderColor: "#111827" };
  if (variant === "danger")
    return { ...base, background: "#b91c1c", color: "white", borderColor: "#b91c1c" };
  if (variant === "ghost") return { ...base, background: "#f3f4f6" };
  return base;
}

function inp() {
  return {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    outline: "none",
    fontSize: 14,
  };
}

function th() {
  return {
    textAlign: "left",
    padding: "12px 14px",
    fontSize: 12,
    opacity: 0.85,
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  };
}

function td() {
  return {
    padding: "12px 14px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
    fontSize: 14,
  };
}

function chk() {
  return {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    border: "1px solid #d1d5db",
    borderRadius: 12,
    background: "white",
    fontSize: 14,
  };
}

function grid2() {
  return {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  };
}

function modalWrap() {
  return {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 50,
  };
}

function modal() {
  return {
    width: "min(900px, 100%)",
    background: "white",
    borderRadius: 14,
    padding: 18,
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
  };
}